/**
 * ResearchActivityLog - Live processing log for keyword research.
 *
 * Displays real-time log entries with elapsed time during keyword research.
 * Follows the same pattern as GenerationActivityLog from blog creation.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Clock, Info, AlertTriangle, XCircle } from 'lucide-react';
import type { ProcessingLogEntry } from '@/types/contentPlanner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResearchActivityLogProps {
  entries: ProcessingLogEntry[];
  startedAt?: string | null;
  isProcessing: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LEVEL_CONFIG = {
  info: { icon: Info, color: 'text-muted-foreground', bg: '' },
  warn: { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  error: { icon: XCircle, color: 'text-destructive', bg: 'bg-destructive/5' },
} as const;

function formatElapsed(ms: number): string {
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatTimeSince(startedAt: string): string {
  return formatElapsed(Date.now() - new Date(startedAt).getTime());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ResearchActivityLog({
  entries,
  startedAt,
  isProcessing,
}: ResearchActivityLogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [elapsedDisplay, setElapsedDisplay] = useState('');

  // Tick elapsed timer every second while processing
  useEffect(() => {
    if (!startedAt || !isProcessing) {
      if (entries.length > 0) {
        setElapsedDisplay(formatElapsed(entries[entries.length - 1].elapsed_ms));
      }
      return;
    }
    const update = () => setElapsedDisplay(formatTimeSince(startedAt));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt, isProcessing, entries]);

  // Auto-scroll to bottom on new entries
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries.length]);

  return (
    <div>
      {/* Header with elapsed time */}
      <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground bg-muted/50">
        <span>Processing Log ({entries.length})</span>
        {elapsedDisplay && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {elapsedDisplay}
          </span>
        )}
      </div>

      {/* Log entries */}
      <div
        ref={scrollRef}
        className="max-h-48 overflow-y-auto divide-y divide-slate-50"
      >
        {entries.map((entry, i) => {
          const config = LEVEL_CONFIG[entry.level] || LEVEL_CONFIG.info;
          const Icon = config.icon;
          return (
            <div
              key={i}
              className={`flex items-start gap-2 px-3 py-1 text-xs ${config.bg}`}
            >
              <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${config.color}`} />
              <span className="text-muted-foreground font-mono w-10 shrink-0 text-right">
                {formatElapsed(entry.elapsed_ms)}
              </span>
              <span className="text-foreground flex-1">{entry.msg}</span>
            </div>
          );
        })}
        {entries.length === 0 && (
          <div className="px-3 py-3 text-center text-xs text-muted-foreground">
            Waiting for activity...
          </div>
        )}
      </div>
    </div>
  );
}
